<!-- eslint-disable vue/no-v-html -->
<template>
  <div v-if="label || title" class="BookingStepper">
    <ai-typo
      v-if="label"
      variant="paragraph-02-regular"
      as="span"
      color="stratos-700"
      class="BookingStepper-label"
      aria-live="polite"
      aria-current="step">
      <span v-html="label" />
    </ai-typo>

    <div v-if="title" class="BookingStepper-title">
      <ai-typo as="h2" variant="heading-01">
        {{ title }}
      </ai-typo>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useBookingStore } from '../../store';

const bookingStore = useBookingStore();
const { currentStep } = useCurrentBookingStep();
const { t } = useI18n();

const multiple = computed(() => bookingStore.numberOfRooms > 1);
const currentRoomBeingFilled = computed(() => bookingStore.rooms.length + 1);
const currentRoomGuestCount = computed(
  () => bookingStore.currentUnconfiguredGuestsCount,
);

const previousStep = ref('');

const label = computed(() => {
  if (!multiple.value) {
    const stepIndex = new Map([
      ['room', 1],
      ['rate', 2],
      ['details', 3],
      ['payment', 4],
    ]);

    return t('booking.step.single', {
      bookingStep: stepIndex.get(currentStep.value),
    });
  }

  switch (currentStep.value) {
    case 'room':
    case 'rate':
      return t(`booking.step.multiple.${currentStep.value}`, {
        guestCount: currentRoomGuestCount.value,
        roomCount: bookingStore.numberOfRooms,
        roomStep: currentRoomBeingFilled.value,
      });

    case 'details':
      return t(`booking.step.multiple.details`);

    case 'payment':
      return t(`booking.step.multiple.payment`);

    default:
      return '';
  }
});

const title = computed(() => {
  const baseKey = `booking.title.${multiple.value ? 'multiple' : 'single'}`;

  if (multiple.value) {
    const titleMap = new Map<string, string>([
      ['room', t(`${baseKey}.room.${currentRoomBeingFilled.value}`)],
      ['rate', t(`${baseKey}.rate.${currentRoomBeingFilled.value}`)],
    ]);

    if (titleMap.has(currentStep.value)) {
      return titleMap.get(currentStep.value);
    }
  }

  if (currentStep.value === 'payment') {
    // The title is handled by the component itself since it rely on internal state
    return '';
  }

  if (currentStep.value) {
    return t(`${baseKey}.${currentStep.value}`);
  }

  return previousStep.value ? t(`${baseKey}.${previousStep.value}`) : '';
});

watch(currentStep, (_newStep, oldStep) => {
  previousStep.value = oldStep;
});
</script>

<style scoped lang="scss">
@use '@/assets/styles/utilities/constants';

.BookingStepper-label {
  margin-bottom: constants.$margin-00;
}

.BookingStepper-title {
  margin-bottom: constants.$margin-04;
}
</style>
